import { Component, HostBinding, Input, AfterViewInit, NgZone, inject, Optional } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RimsStepCardComponent } from '../rims-step-card/rims-step-card.component';
import { AddRimsStepModalComponent } from './add-rims-step-modal/add-rims-step-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { RimsPhaseInfo, RimsStepInfo } from '@app/models/rims.model';
import { IncidentDetailsPageStore } from '@app/pages/incidents/details-page/incident-details-page.store';
import { createRimsStepFromSchema } from '@app/utils/rims/rims.utils';
import { RimsService } from '@app/services/rims/rims.service';
import { TechnicalStepModel } from '@app/models/technical-step.model';

// Disclaimer: This component is used in two different contexts: IncidentDetailsPage and RimsPage. In the RimsPage we only use it as "read-only" view to display the Rims structure.
// For now, we decided to keep the RIMS components inside the IncidentDetailsPage folder, as they are mainly used there. If we want to use them in other contexts, we can move them to a more generic location in the future.
@Component({
  standalone: true,
  selector: 'app-rims-phase-card',
  imports: [
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatButtonModule,
    MatCardModule,
    MatDividerModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatListModule,
    MatSelectModule,
    MatTooltipModule,
    RimsStepCardComponent,
  ],
  templateUrl: './rims-phase-card.component.html',
  styleUrl: './rims-phase-card.component.scss',
})
export class RimsPhaseCardComponent implements AfterViewInit {
  private readonly dialog = inject(MatDialog);

  @HostBinding('class.expanded') protected expanded = false;
  protected get sortedRimsStepInfos(): RimsStepInfo[] {
    // RimsPage - Get all step infos when no steps are provided
    // IncidentDetails - Get only the step infos of the steps provides
    const rimsStepInfos = !this.technicalSteps
      ? this.rimsPhaseInfo?.rimsStepInfos
      : this.rimsPhaseInfo?.rimsStepInfos.filter((rimsStepInfo) =>
          this.technicalSteps?.some((technicalStep) => technicalStep.rimsStep.$type === rimsStepInfo.stepId),
        );

    return [...(rimsStepInfos ?? [])].sort((a, b) => a.stepId.localeCompare(b.stepId));
  }

  @Input() public rimsPhaseInfo?: RimsPhaseInfo;
  @Input() public technicalSteps?: TechnicalStepModel[];

  constructor(
    private readonly rimsService: RimsService,
    private readonly ngZone: NgZone,
    @Optional() protected readonly incidentsPageStore?: IncidentDetailsPageStore, // This is marked as Optional() as this component is also used to display the Rims structure in the RimsPage
  ) {}

  ngAfterViewInit(): void {
    this.ngZone.run(() => {
      setTimeout(() => {
        this.expanded = !!this.rimsPhaseInfo?.rimsStepInfos.length;
      });
    });
  }

  protected async openAddRimsStepModalAsync(): Promise<void> {
    // Get all steps that are not yet added to the phase
    const availableRimsStepInfos: RimsStepInfo[] =
      this.rimsPhaseInfo?.rimsStepInfos
        .filter(
          (rimsStepInfo) =>
            !this.technicalSteps?.some((technicalStep) => technicalStep.rimsStep.$type === rimsStepInfo.stepId),
        )
        .sort((a, b) => a.stepId.localeCompare(b.stepId)) ?? [];

    const dialogRef = this.dialog.open(AddRimsStepModalComponent, {
      width: '90%',
      maxWidth: '1200px',
      data: {
        rimsStepInfos: availableRimsStepInfos,
      },
    });
    const rimsStepIdToAdd: string | undefined = await firstValueFrom(dialogRef.afterClosed());
    if (rimsStepIdToAdd) {
      const rimsStepSchema = await firstValueFrom(this.rimsService.getRimsStepSchema(rimsStepIdToAdd));
      const technicalStep: TechnicalStepModel = {
        id: 0,
        incidentId: this.incidentsPageStore!.incident()!.id,
        articleReferenceId: null,
        sourceReferenceId: null,
        rimsStep: createRimsStepFromSchema(rimsStepSchema, false),
      };
      this.technicalSteps?.push(technicalStep);
      this.incidentsPageStore!.isRimsEdited = true;
      // If there were no steps and we add the first one, we want to expand the view
      if (!this.expanded) {
        this.expanded = true;
      }
    }
  }

  protected onExpandToggle(): void {
    this.expanded = !this.expanded;
  }
}
